import {NgFor, NgIf, AsyncPipe, NgClass} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output
} from '@angular/core';
import {HttpParams} from '@angular/common/http';
import {ActivatedRoute} from '@angular/router';
import {MatListModule} from '@angular/material/list';
import {catchError, map, tap} from 'rxjs/operators';
import {combineLatest, EMPTY, from, Observable} from 'rxjs';
import {TranslateService, TranslateModule} from '@ngx-translate/core';
import {get} from 'lodash';
import {
  DialogWidth,
  IntercomService,
  PermissionsService,
  Q9TransformPipe,
  Release,
  SessionService,
  SnackBarService,
  TakeUntilComponentDestroy
} from '@q9elements/ui-kit/common';
import {VersionsService} from '../../../../core/services/versions.service';
import {MakeReadyForReleasePopupComponent} from './make-ready-for-release-popup/make-ready-for-release-popup.component';
import {PopupManagerService} from '../../../../core/services/popup-manager.service';
import {RequestsService} from '../../../../core/requests/requests.service';
import {NavigationService} from '../../../../core/navigation/navigation.service';
import {DiagramJsonHandlerService} from '../../../../diagram-data/diagram-json-handler.service';
import {ModeDataService} from '../../../../editor-state/mode-data.service';
import {CustomEncoder} from '../../../../core/helpers/custom-encoder';
import {RequestAuthorisationPopupComponent} from './request-authorisation-popup/request-authorisation-popup.component';
import {DIAGRAM_VERSION_STATES_ENUM} from '../../models/diagram-version.constant';
import {ERROR_STATUSES} from '../../../../core/values/error-statuses';
import {AuthorisationService} from '../../../../core/services/authorisation.service';
import {RequestTask} from 'src/app/shared/models/request-task.interface';
import {VersionListItemComponent} from './version-list-item/version-list-item.component';
import {DiagramVersionItem} from '../../../sidebar/shared/models/diagram-version-item.interface';
import {ScreenshotPopoverDirective} from '../../directives/screenshot-popover.directive';
import {IsAuthorisationInProgressPipe} from '../is-authorisation-in-progress.pipe';

// @dynamic
@Component({
  selector: 'app-versions-list',
  templateUrl: './versions-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgFor,
    ScreenshotPopoverDirective,
    NgIf,
    NgClass,
    AsyncPipe,
    TranslateModule,
    MatListModule,
    VersionListItemComponent,
    Q9TransformPipe
  ],
  providers: [IsAuthorisationInProgressPipe]
})
export class VersionsListComponent extends TakeUntilComponentDestroy implements OnInit, OnChanges {
  @Input() versions: DiagramVersionItem[];
  @Input() diagramId: string;
  @Input() isSideBar: boolean;

  @Output() navigation = new EventEmitter<any>();

  public activeVersion: DiagramVersionItem;
  public anchorTarget: string;
  public diagramJson$ = this.diagramJsonSvc.diagramJson$;
  public isAuthorizationActive$: Observable<boolean> = combineLatest([
    this.diagramJsonSvc.isEnterpriseActive$,
    this.versionsSvc.authorisation$
  ]).pipe(
    this.takeUntilComponentDestroy(),
    map(([isEnterpriseActive, isAuthorisationEnabled]) => {
      return isEnterpriseActive && isAuthorisationEnabled;
    })
  );
  public isAuthorisationInProgress = false;
  public beingAuthorisedState = DIAGRAM_VERSION_STATES_ENUM.BEING_AUTHORISED;
  public readyForReleaseState = DIAGRAM_VERSION_STATES_ENUM.READY;
  public draft = DIAGRAM_VERSION_STATES_ENUM.DRAFT;

  protected readonly intercomService = inject(IntercomService);
  protected readonly sessionService = inject(SessionService);
  protected readonly permissionsService = inject(PermissionsService);

  constructor(
    private versionsSvc: VersionsService,
    private popupSvc: PopupManagerService,
    private translateSvc: TranslateService,
    private snackBarSvc: SnackBarService,
    private requestsSvc: RequestsService,
    private navigationSvc: NavigationService,
    private diagramJsonSvc: DiagramJsonHandlerService,
    private modeDataSvc: ModeDataService,
    private route: ActivatedRoute,
    private authorisationService: AuthorisationService,
    private isAuthorisationInProgressPipe: IsAuthorisationInProgressPipe
  ) {
    super();
  }

  ngOnInit() {
    this.diagramJsonSvc.diagramJson$.pipe(this.takeUntilComponentDestroy()).subscribe(diagram => {
      this.activeVersion = this.versions.find(version => {
        return version.version === diagram.version && version.state === diagram.state;
      });
    });
  }

  ngOnChanges() {
    this.checkForActiveReleaseTags(this.versions);
    this.isAuthorisationInProgress = this.isAuthorisationInProgressPipe.transform(this.versions);
  }

  getDraftDiagramRelease(data) {
    const draftVersion = this.versions.find(
      version => version.state === DIAGRAM_VERSION_STATES_ENUM.DRAFT
    );

    return data.releases.find(release => release.id === draftVersion?.release?.id);
  }

  checkForActiveReleaseTags(versions) {
    versions.map(version => {
      version.releaseTags.map((releaseTag, index) => {
        if ('r_' + releaseTag.id === this.queryParams.v) {
          if (releaseTag.id !== version.release.id) {
            this.versionsSvc.navigationByReleaseTag = true;
          }

          if (index > 2) {
            version.moreShown = true;
          }
        }
      });
    });
  }

  get queryParams() {
    return this.route.snapshot.queryParams;
  }

  public onTurnOnReadyAuthorization(value: boolean): void {
    const task = this.taskForMakeReadyForAuthorisation;

    task.data.readyToAuthorise = value;
    this.requestsSvc
      .addToQueue(task, true)
      .then(dv => {
        const versionToUpdate = this.versions.find(
          v => v.state === DIAGRAM_VERSION_STATES_ENUM.DRAFT
        );
        const newVersion = {...versionToUpdate, readyToAuthorise: value};

        this.versions = [
          newVersion,
          ...this.versions.filter(v => v.state !== DIAGRAM_VERSION_STATES_ENUM.DRAFT)
        ];
      })
      .catch(reject => this.handleReadyForAuthorisationError(reject));
  }

  onMakeReadyForRelease(): void {
    this.openActionForReleasePopup(false);
  }

  private getReleasesObservable() {
    return from(
      this.requestsSvc.addToQueue(VersionsListComponent.taskForActionForRelease, true)
    ).pipe(
      tap((res: {draftDiagramRelease: Release}) => {
        res.draftDiagramRelease = this.getDraftDiagramRelease(res);
      }),
      catchError(err => {
        this.snackBarSvc.error(err.message || 'GENERAL.ERROR');

        return EMPTY;
      })
    );
  }

  openActionForReleasePopup(fromAssign: boolean): void {
    const dialogRef = this.popupSvc.open(MakeReadyForReleasePopupComponent, {
      panelClass: ['q9-dialog'],
      width: DialogWidth.MD,
      data: {
        releases$: this.getReleasesObservable(),
        isFromAssign: fromAssign
      }
    });

    dialogRef.afterClosed().subscribe(success => {
      if (success) {
        this.snackBarSvc.success(this.translateSvc.instant('VERSIONING.MAKE_READY_SUCCESS'), '');

        this.intercomService.fireEvent(
          'ACTION',
          this.intercomService.EVENTS_BY_AREA.DIAGRAMS.DIAGRAM_MADE_READY_FOR_RELEASE_CONFIRMED,
          {
            space_id: this.sessionService.teamId(),
            actor_user_id: this.sessionService.userId(),
            account_id: this.permissionsService.team()?.domain,
            event_date: new Date().toISOString(),
            product_area: this.intercomService.PRODUCT_AREAS.DIAGRAMS
          }
        );
      }
    });
  }

  openSubmitForAuthorizationPopup(): void {
    const dialogRef = this.popupSvc.open(RequestAuthorisationPopupComponent, {
      panelClass: ['q9-dialog'],
      width: DialogWidth.MD,
      data: this.getReleasesObservable()
    });

    dialogRef.afterClosed().subscribe(response => {
      if (response) {
        switch (true) {
          case response.success:
            this.diagramJsonSvc.update({changeIsReadyToAuthorise: {value: response.value}});
            this.snackBarSvc.success(this.translateSvc.instant(response.message), '');
            break;

          case response.needReload: {
            const {message = 'GENERAL.ERROR'} = response;

            this.snackBarSvc.error(message);
            this.versionsSvc.getVersions();
            break;
          }
        }
      }
    });
  }

  static get taskForActionForRelease(): RequestTask {
    const params = new HttpParams({
      fromString: `state=${'active'}&limit=999&skip=0`,
      encoder: new CustomEncoder()
    });

    return {
      isApi: true,
      method: 'GET',
      url: `/releases`,
      queryParams: params
    };
  }

  get taskForMakeReadyForAuthorisation(): RequestTask {
    const data = {
      type: 'diagram'
    };

    return {
      isApi: true,
      method: 'PUT',
      url: `/authorisation/${this.diagramJsonSvc.get().diagramId}/ready`,
      data
    };
  }

  navigateToVersion(version: DiagramVersionItem) {
    this.activeVersion = version;
    const diagramId = this.diagramId ? this.diagramId : this.diagramJsonSvc.get().diagramId;
    const mode =
      version.state === DIAGRAM_VERSION_STATES_ENUM.DRAFT ? this.modeDataSvc.isEditMode() : false;

    if (version.state !== DIAGRAM_VERSION_STATES_ENUM.ARCHIVE) {
      this.navigate(diagramId, version.state, mode, this.versionsSvc.getToolbarVersion(version));
    } else {
      this.navigate(
        diagramId,
        'r_' + version.release.id,
        mode,
        this.versionsSvc.getToolbarVersion(version)
      );
    }
  }

  navigate(id, state, mode, versionName, byReleaseTag?) {
    this.versionsSvc.navigationByReleaseTag = byReleaseTag;
    this.navigation.emit();
    let navPromise;
    const exitAuthorisationMode = this.authorisationService.authorisationClosed();

    switch (true) {
      case exitAuthorisationMode:
        navPromise = this.navigationSvc.navigateWithoutQuery(id, state);
        break;

      default:
        navPromise = this.navigationSvc.navigate(id, state, mode);
        break;
    }

    navPromise.then(
      () => {
        this.versionsSvc.currVersionSubject.next(versionName);
        this.versionsSvc.getMapAuthorisationMode();
      },
      () => {}
    );
  }

  getConfigForScreen(version: DiagramVersionItem) {
    const state =
      version.state === DIAGRAM_VERSION_STATES_ENUM.ARCHIVE
        ? 'r_' + version.release.id
        : version.state;

    return {
      id: this.diagramJsonSvc.get().diagramId,
      state: state,
      size: '_600x400'
    };
  }

  private handleReadyForAuthorisationError(reject) {
    const status = get(reject, 'error.status');

    switch (status) {
      case ERROR_STATUSES.MAP.ERROR.NOT_EXIST:
        return this.popupSvc.openErrorDialog({
          message: 'POPUPS.NO_ACCESS',
          callback: 'navigateToDashboard'
        });

      case ERROR_STATUSES.MAP.ERROR.AUTHORISATION_NOT_ENABLED:
        this.snackBarSvc.error(get(reject, 'error.error') || 'GENERAL.ERROR');

        return this.versionsSvc.getMapAuthorisationMode();

      default:
        this.snackBarSvc.error(get(reject, 'error.error') || 'GENERAL.ERROR');
    }
  }
}
